import React, { useEffect, useState } from "react";
import axios from 'axios';
import { API_BASE_URL } from "../config";
import { useAuth } from '../components/shared/AuthContext';
import { LogoLoader } from "../components/ui";

const LedgerSection = ({ onBack }) => {
  const { permissions, roleName, data } = useAuth();
  const userRole = roleName === 'superadmin' ? 'superadmin' : 'other';
  const loginId = data?.id;
  const branchId = data?.branchId; // Get branchId from useAuth
  const [activeTab, setActiveTab] = useState(userRole === 'superadmin' ? 'base_group' : 'account');
  const [ledgerAccounts, setLedgerAccounts] = useState([]);
  const [ledgerTransactions, setLedgerTransactions] = useState([]);
  const [baseGroups, setBaseGroups] = useState([]);
  const [staffDetails, setStaffDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ officer_Id: loginId, branchId: branchId }); // Add branchId to initial formData

  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  useEffect(() => {
    loadLedgerData();
    if (activeTab === 'transaction') {
      loadAccountsForDropdown();
      loadStaffDetails();
      loadBaseGroupsForDropdown();
    } else if (activeTab === 'account') {
      loadBaseGroupsForDropdown();
    }
  }, [activeTab]);

  const loadBaseGroupsForDropdown = async () => {
    try {
      const response = await axios.get('/api/getModelData/base_ledger_group', {
        baseURL: API_BASE_URL
      });
      const groupsArray = Array.isArray(response.data?.data) ? response.data.data : [];
      setBaseGroups(groupsArray);
    } catch (error) {
      console.error('Error loading base groups for dropdown:', error);
      setBaseGroups([]);
    }
  };

  const loadAccountsForDropdown = async () => {
    try {
      const response = await axios.get('/api/getModelData/ledger_account?includeDepth=1', {
        baseURL: API_BASE_URL
      });
      const accountsArray = Array.isArray(response.data?.data) ? response.data.data : [];
      setLedgerAccounts(accountsArray);
    } catch (error) {
      console.error('Error loading accounts for dropdown:', error);
      setLedgerAccounts([]);
    }
  };

  const loadStaffDetails = async () => {
    try {
      const response = await axios.get('/api/getModelData/staff_details', {
        baseURL: API_BASE_URL
      });
      const staffArray = Array.isArray(response.data) ? response.data :
        response.data?.data ? (Array.isArray(response.data.data) ? response.data.data : []) : [];
      setStaffDetails(staffArray);
    } catch (error) {
      console.error('Error loading staff details:', error);
      setStaffDetails([]);
    }
  };

  const loadLedgerData = async () => {
    setLoading(true);
    try {
      if (userRole === 'superadmin' && activeTab === 'base_group') {
        const response = await axios.get('/api/getModelData/base_ledger_group', {
          baseURL: API_BASE_URL
        });
        const groupsArray = Array.isArray(response.data?.data) ? response.data.data : [];
        setBaseGroups(groupsArray);
      } else if (activeTab === 'account') {
        const response = await axios.get('/api/getModelData/ledger_account?includeDepth=1', {
          baseURL: API_BASE_URL
        });
        const accountsArray = Array.isArray(response.data?.data) ? response.data.data : [];
        setLedgerAccounts(accountsArray);
      } else {
        const response = await axios.get('/api/getModelData/ledger_account?includeDepth=1', {
          baseURL: API_BASE_URL
        });
        const accountsArray = Array.isArray(response.data?.data) ? response.data.data : [];
        const transactions = accountsArray.reduce((acc, account) => {
          const accountTransactions = Array.isArray(account.fk_ledger_transaction_hasMany_ledger_account_accountId)
            ? account.fk_ledger_transaction_hasMany_ledger_account_accountId
            : [];
          return [...acc, ...accountTransactions];
        }, []);
        setLedgerTransactions(transactions);
      }
    } catch (error) {
      console.error('Error loading ledger data:', error);
      if (userRole === 'superadmin' && activeTab === 'base_group') {
        setBaseGroups([]);
      } else if (activeTab === 'account') {
        setLedgerAccounts([]);
      } else {
        setLedgerTransactions([]);
      }
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let modelName;
      const requestData = {
        type: editingItem ? 'Edit' : 'Add'
      };

      if (userRole === 'superadmin' && activeTab === 'base_group') {
        modelName = 'base_ledger_group';
        requestData.model = modelName;
        requestData.group_name = formData.group_name;
        requestData.group_type = formData.group_type;
        requestData.status = formData.status ?? true;
      } else if (activeTab === 'account') {
        modelName = 'ledger_account';
        requestData.model = modelName;
        requestData.name = formData.name;
        requestData.account_type = 1;
        requestData.ledger_id = parseInt(formData.ledger_id);
      } else {
        modelName = 'ledger_transaction';
        requestData.model = modelName;
        requestData.accountId = parseInt(formData.accountId);
        requestData.officer_Id = parseInt(formData.officer_Id);
        requestData.branchId = parseInt(branchId); // Add branchId to transaction request
        requestData.date = formData.date || getTodayDate();
        requestData.particulars = formData.particulars;
        requestData.transaction_type = formData.type;
        requestData.amount = parseInt(parseFloat(formData.amount) * 100);
        requestData.balanceAfter = 0;
        requestData.destination_ledger = formData.destination_ledger ? parseInt(formData.destination_ledger) : null;
      }

      if (editingItem) {
        requestData.id = editingItem.id;
      }

      await axios.post('/api/addDetails', requestData, {
        baseURL: API_BASE_URL
      });

      await loadLedgerData();
      setShowForm(false);
      setEditingItem(null);
      setFormData({ officer_Id: loginId, branchId: branchId }); // Reset formData with branchId

      alert(editingItem ? 'Updated successfully!' : 'Added successfully!');
    } catch (error) {
      console.error('Error saving data:', error);
      alert('Error saving data. Please try again.');
    }
    setLoading(false);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      ...item,
      officer_Id: loginId,
      branchId: branchId, // Include branchId in edit form
      amount: item.amount ? (item.amount / 100).toFixed(2) : ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingItem(null);
    setFormData({ officer_Id: loginId, branchId: branchId }); // Reset with branchId
  };

  const renderBaseGroupForm = () => (
    <div className="card">
      <div className="card-header bg-success text-white">
        <h5 className="mb-0">
          {editingItem ? 'Edit' : 'Add'} Base Ledger Group
        </h5>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">Group Type</label>
              <select
                className="form-select"
                value={formData.group_type || ''}
                onChange={(e) => setFormData({ ...formData, group_type: e.target.value })}
                required
              >
                <option value="Primary">Primary</option>
                <option value="Secondary">Secondary</option>
                <option value="DirectIncome">Direct Income</option>
                <option value="IndirectIncome">Indirect Income</option>
                <option value="CommissionReceived">Commission Received</option>
                <option value="Rent">Rent</option>
                <option value="InterestIncome">Interest Income</option>
                <option value="OtherIncome">Other Income</option>
                <option value="DirectExpenses">Direct Expenses</option>
                <option value="IndirectExpenses">Indirect Expenses</option>
                <option value="Salary">Salary</option>
                <option value="Electricity">Electricity</option>
                <option value="OfficeExpenses">Office Expenses</option>
                <option value="FixedAssets">Fixed Assets</option>
                <option value="CurrentAssets">Current Assets</option>
                <option value="BankAccount">Bank Account</option>
                <option value="SundryDebtors">Sundry Debtors</option>
                <option value="CapitalAccount">Capital Account</option>
                <option value="CurrentLiabilities">Current Liabilities</option>
                <option value="SundryCreditors">Sundry Creditors</option>
                <option value="Loans">Loans</option>
                <option value="DutiesTaxes">Duties & Taxes</option>
                <option value="Provisions">Provisions</option>
              </select>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Group Name</label>
              <input
                type="text"
                className="form-control"
                value={formData.group_name || ''}
                onChange={(e) => setFormData({ ...formData, group_name: e.target.value })}
                required
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={formData.status === undefined ? true : formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value === 'true' })}
                required
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>
          <div className="d-flex gap-2">
            <button type="submit" className="btn btn-success" disabled={loading}>
              {loading ? 'Saving...' : (editingItem ? 'Update' : 'Add')}
            </button>
            <button type="button" className="btn btn-secondary" onClick={resetForm}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const renderAccountForm = () => (
    <div className="card">
      <div className="card-header bg-primary text-white">
        <h5 className="mb-0">
          {editingItem ? 'Edit' : 'Add'} Ledger Account
        </h5>
      </div>

      <div className="card-body">
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">Ledger Group</label>
              <select
                className="form-select"
                value={formData.ledger_id || ''}
                onChange={(e) => setFormData({ ...formData, ledger_id: e.target.value })}
                required
              >
                <option value="">Select Group</option>
                {baseGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.group_name} ({group.group_type})
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Account Name</label>
              <input
                type="text"
                className="form-control"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>

          </div>
          <div className="d-flex gap-2">
            <button type="submit" className="btn btn-success" disabled={loading}>
              {loading ? 'Saving...' : (editingItem ? 'Update' : 'Add')}
            </button>
            <button type="button" className="btn btn-secondary" onClick={resetForm}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const renderTransactionForm = () => {
    const currentAssetsGroup = baseGroups.find(group => group.group_type === 'CurrentAssets');
    const currentAssetsGroupId = currentAssetsGroup ? currentAssetsGroup.id : null;
    const currentAssetsAccounts = ledgerAccounts.filter(account =>
      account.ledger_id === currentAssetsGroupId && account.name && account.id
    );

    return (
      <div className="card">
        <div className="card-header bg-info text-white">
          <h5 className="mb-0">
            {editingItem ? 'Edit' : 'Add'} Ledger Transaction
          </h5>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Mode of Payment</label>
                <select
                  className="form-select"
                  value={formData.accountId || ''}
                  onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
                  required
                >
                  <option value="">SELECT MODE OF PAYMENT</option>
                  {currentAssetsAccounts.length > 0 ? (
                    currentAssetsAccounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.name.toUpperCase()} ({account.account_type ? account.account_type.toUpperCase() : 'N/A'})
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>No Current Assets Accounts Available</option>
                  )}
                </select>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Destination Ledger</label>
                <select
                  className="form-select"
                  value={formData.destination_ledger || ''}
                  onChange={(e) => setFormData({ ...formData, destination_ledger: e.target.value })}
                >
                  <option value="">SELECT DESTINATION LEDGER</option>
                  {ledgerAccounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name.toUpperCase()} ({account.account_type ? account.account_type.toUpperCase() : 'N/A'})
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Officer</label>
                <input
                  type="text"
                  className="form-control"
                  value={staffDetails.find(staff => staff.id === loginId)?.name ||
                    staffDetails.find(staff => staff.id === loginId)?.username ||
                    'UNKNOWN'}
                  readOnly
                />
                <input
                  type="hidden"
                  name="officer_Id"
                  value={loginId}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Branch ID</label>
                <input
                  type="text"
                  className="form-control"
                  value={branchId || 'N/A'}
                  readOnly
                />
                <input
                  type="hidden"
                  name="branchId"
                  value={branchId}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Transaction Type</label>
                <select
                  className="form-select"
                  value={formData.type || ''}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  required
                >
                  <option value="">SELECT TYPE</option>
                  <option value="payment">Payment</option>
                  <option value="receipt">Receipt</option>
                </select>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Amount</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control"
                  value={formData.amount || ''}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.date || getTodayDate()}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                />
              </div>
              <div className="col-12 mb-3">
                <label className="form-label">Particulars</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={formData.particulars || ''}
                  onChange={(e) => setFormData({ ...formData, particulars: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="d-flex gap-2">
              <button type="submit" className="btn btn-success" disabled={loading}>
                {loading ? 'Saving...' : (editingItem ? 'Update' : 'Add')}
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const renderDataTable = () => {
    const data = activeTab === 'base_group' ? baseGroups :
      activeTab === 'account' ? ledgerAccounts :
        ledgerTransactions;

    if (loading) {
      return (
        <div className="flex justify-center py-8">
          <LogoLoader size="md" />
        </div>
      );
    }

    if (data.length === 0) {
      return (
        <div className="alert alert-info text-center">
          No {activeTab === 'base_group' ? 'groups' : activeTab === 'account' ? 'accounts' : 'transactions'} found.
        </div>
      );
    }

    return (
      <div className="table-responsive">
        <table className="table table-striped table-bordered table-hover">
          <thead className="table-light">
            <tr>
              {activeTab === 'base_group' ? (
                <>
                  <th>ID</th>
                  <th>Group Name</th>
                  <th>Group Type</th>
                  <th>Status</th>
                  <th>Actions</th>
                </>
              ) : activeTab === 'account' ? (
                <>
                  <th>ID</th>
                  <th>Account Name</th>
                  <th>Ledger Group</th>
                  <th>Actions</th>
                </>
              ) : (
                <>
                  <th>ID</th>
                  <th>Mode of Payment</th>
                  <th>Destination Ledger</th>
                  <th>Officer</th>
                  <th>Branch ID</th>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Particulars</th>
                  <th>Actions</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => (
              <tr key={item.id || index}>
                {activeTab === 'base_group' ? (
                  <>
                    <td data-label="ID">{item.id}</td>
                    <td data-label="Group Name">{item.group_name}</td>
                    <td data-label="Group Type">
                      <span className={`badge bg-${item.group_type === 'Primary' ? 'primary' : 'secondary'}`}>
                        {item.group_type}
                      </span>
                    </td>
                    <td data-label="Status">
                      <span className={`badge bg-${item.status ? 'success' : 'danger'}`}>
                        {item.status ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td data-label="Actions">
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => handleEdit(item)}
                      >
                        Edit
                      </button>
                    </td>
                  </>
                ) : activeTab === 'account' ? (
                  <>
                    <td data-label="ID">{item.id}</td>
                    <td data-label="Account Name">{item.name}</td>
                    <td data-label="Ledger Group">{baseGroups.find(group => group.id === item.ledger_id)?.group_name || 'N/A'}</td>
                    <td data-label="Actions">
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => handleEdit(item)}
                      >
                        Edit
                      </button>
                    </td>
                  </>
                ) : (
                  <>
                    <td data-label="ID">{item.id}</td>
                    <td data-label="Mode">{ledgerAccounts.find(acc => acc.id === item.accountId)?.name || 'Unknown'}</td>
                    <td data-label="Destination">{ledgerAccounts.find(acc => acc.id === item.destination_ledger)?.name || 'N/A'}</td>
                    <td data-label="Officer">
                      {staffDetails.find(staff => staff.id === item.officer_Id)?.name ||
                        staffDetails.find(staff => staff.id === item.officer_Id)?.username ||
                        'Unknown'}
                    </td>
                    <td data-label="Branch">{item.branchId || 'N/A'}</td>
                    <td data-label="Date">{item.date}</td>
                    <td data-label="Type">
                      <span className={`badge bg-${item.transaction_type === 'receipt' ? 'success' : 'danger'}`}>
                        {item.transaction_type}
                      </span>
                    </td>
                    <td data-label="Amount">₹{(item.amount / 100)?.toFixed(2) || '0.00'}</td>
                    <td data-label="Particulars">{item.particulars}</td>
                    <td data-label="Actions">
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => handleEdit(item)}
                      >
                        Edit
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="container-fluid py-4">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="text-primary mb-0">
              <i className="fas fa-book me-2"></i>
              Ledger Management
            </h2>
          </div>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-12">
          <ul className="nav nav-pills nav-fill d-flex flex-wrap gap-2">
            {userRole === 'superadmin' && (
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'base_group' ? 'active' : ''}`}
                  onClick={() => setActiveTab('base_group')}
                >
                  <i className="fas fa-layer-group me-2"></i>
                  Base Groups
                </button>
              </li>
            )}
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'account' ? 'active' : ''}`}
                onClick={() => setActiveTab('account')}
              >
                <i className="fas fa-user-circle me-2"></i>
                Ledger Accounts
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'transaction' ? 'active' : ''}`}
                onClick={() => setActiveTab('transaction')}
              >
                <i className="fas fa-exchange-alt me-2"></i>
                Transactions
              </button>
            </li>
          </ul>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-12 d-flex flex-wrap gap-2">
          <button
            className="btn btn-primary"
            onClick={() => setShowForm(true)}
          >
            <i className="fas fa-plus me-2"></i>
            Add {activeTab === 'base_group' ? 'Group' : activeTab === 'account' ? 'Account' : 'Transaction'}
          </button>
          <button
            className="btn btn-outline-primary"
            onClick={loadLedgerData}
            disabled={loading}
          >
            <i className="fas fa-sync-alt me-2"></i>
            Refresh
          </button>
        </div>
      </div>

      {showForm && (
        <div className="row mb-4">
          <div className="col-12">
            {activeTab === 'base_group' && userRole === 'superadmin' ? renderBaseGroupForm() :
              activeTab === 'account' ? renderAccountForm() :
                renderTransactionForm()}
          </div>
        </div>
      )}

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header bg-light">
              <h5 className="mb-0">
                {activeTab === 'base_group' ? 'Base Ledger Groups' :
                  activeTab === 'account' ? 'Ledger Accounts' :
                    'Ledger Transactions'}
              </h5>
            </div>
            <div className="card-body">
              {renderDataTable()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LedgerSection;